// @flow
import './index.less';

import {Skeleton} from 'antd';
import {Trans, useTranslation} from 'gatsby-plugin-react-i18next';
import React, {useCallback, useEffect, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import PaymentMethod from '_components/PaymentMethod';
import PaymentMethodAddForm from '_components/PaymentMethodAddForm';
import {useAuth, useAuthState} from '_contexts/auth';
import shieldCheckFill from '_images/shield-check-fill.png';
import {setDefaultPaymentMethod} from '_utils/stripe';

type Props = {
  active?: boolean,
  editable?: boolean,
  selectable?: boolean,
  showAddFormOnEmptyPaymentMethods?: boolean,
  backgroundColor?: string,
  selectedPaymentMethod?: Object,
  onSelectPaymentMethod?: (paymentMethod: Object) => void,
};

const PaymentManager = ({
  active,
  editable = false,
  selectable = false,
  showAddFormOnEmptyPaymentMethods = false,
  backgroundColor,
  selectedPaymentMethod,
  onSelectPaymentMethod = () => {},
}: Props): React$Node => {
  const {loading, currentUser} = useAuthState();
  const {restore} = useAuth();
  const {t} = useTranslation();
  const [showAddPaymentMethodForm, setShowAddPaymentMethodForm] = useState(false);
  const [showAddPaymentMethodFormHeader, setShowAddPaymentMethodFormHeader] = useState();

  const paymentMethods = currentUser?.stripeCustomer?.paymentMethods;
  const defaultPaymentMethodId = currentUser?.stripeCustomer?.invoiceSettings?.defaultPaymentMethod?.id;

  useEffect(() => {
    if (loading) return;
    if (showAddFormOnEmptyPaymentMethods && !paymentMethods) {
      setShowAddPaymentMethodForm(true);
      setShowAddPaymentMethodFormHeader(false);
    } else {
      setShowAddPaymentMethodForm(false);
    }
  }, [loading, paymentMethods, showAddFormOnEmptyPaymentMethods]);

  const handleSelectPaymentMethod = paymentMethod => {
    onSelectPaymentMethod(paymentMethod);
  };

  useEffect(() => {
    if (loading) return;
    if (selectable && defaultPaymentMethodId) {
      handleSelectPaymentMethod(paymentMethods?.find(pm => pm.id === defaultPaymentMethodId));
    }
  }, [loading, selectable, defaultPaymentMethodId]);

  const onAddNewPaymentMethodSuccess = useCallback(
    async (paymentMethod: Object) => {
      setShowAddPaymentMethodForm(false);
      // first payment method should be set as default
      if (!paymentMethods) {
        await setDefaultPaymentMethod(uuidv4(), paymentMethod.id);
      }
      await restore();
      if (selectable) {
        handleSelectPaymentMethod(paymentMethod);
      }
    },
    [restore, paymentMethods],
  );

  const handleAddNewCard = useCallback(() => {
    setShowAddPaymentMethodForm(true);
    setShowAddPaymentMethodFormHeader(true);
  }, []);

  if (loading) {
    return (
      <div className="payment-manager-wrapper">
        <Skeleton active />
      </div>
    );
  }

  const styleObject = {};

  if (backgroundColor) {
    styleObject.backgroundColor = backgroundColor;
  }

  return (
    <div className="payment-manager-wrapper">
      <div className="payment-method-manager" style={styleObject}>
        {paymentMethods && (
          <div className="payment-method-list">
            {paymentMethods.map(pm => (
              <div className="payment-method-item" key={pm.id}>
                <PaymentMethod
                  paymentMethod={pm}
                  selected={selectedPaymentMethod?.id === pm.id}
                  editable={editable}
                  selectable={selectable}
                  onSelect={handleSelectPaymentMethod}
                  isDefault={defaultPaymentMethodId === pm.id}
                />
              </div>
            ))}
          </div>
        )}
        {!showAddPaymentMethodForm && active && (
          <div className="add-new-card-button-wrapper">
            <div>{!paymentMethods && <div className="cards-not-found">No Cards Found</div>}</div>
            <span className="add-new-card-button" onClick={handleAddNewCard}>
              {t('Common_AddNewCard')}
            </span>
          </div>
        )}
        {showAddPaymentMethodForm && (
          <PaymentMethodAddForm
            disabled={!active}
            showHeader={showAddPaymentMethodFormHeader}
            onSuccess={onAddNewPaymentMethodSuccess}
            onCancel={() => setShowAddPaymentMethodForm(false)}
          />
        )}
      </div>
      <div className="payment-method-secure-note">
        <img className="shield-check-icon" src={shieldCheckFill} alt="" />
        <span>
          <Trans i18nKey="PaymentManager_StripeSecureNote">
            Payment information stored securely via
            <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">
              {' '}
              Stripe.
            </a>
          </Trans>
        </span>
      </div>
    </div>
  );
};

export default PaymentManager;
